import { useCallback, useEffect, useRef, useState } from 'react';
import { getSocket } from '../socketManager';

const Status = {
    IDLE: 'idle',
    STREAMING: 'streaming',
    DONE: 'done',
    ERROR: 'error',
};

const SocketEvents = {
    CHUNK: 'ai:chunk',
    DONE: 'ai:done',
    ERROR: 'ai:error',
};

const initialStreamState = () => ({
    conversationId: null,
    summary: '',
});

/**
 * Subscribes to AI streaming events for a single Summary text generation.
 * Each chunk extends the accumulated summary string and `onUpdate({ content })`
 * is fired so the caller can write the latest content into the form field.
 *
 * Lifecycle:
 *  - `start(conversationId)` is idempotent; calling with a new id resets state.
 *  - Cleanup on `ai:done` / `ai:error` and on hook unmount.
 *  - `onUpdate` is read via a ref so socket listeners bind exactly once per
 *    mount and never miss chunks during callback identity changes.
 *
 * Multi-server safe: the api/ instance the user's socket is on owns the
 * Redis subscription for this conversation, so chunks reach exactly one
 * client.
 *
 * @param {object} params
 * @param {(payload: { content: string }) => void} params.onUpdate
 */
export const useAiSessionNoteStream = ({ onUpdate }) => {
    const [status, setStatus] = useState(Status.IDLE);
    const [error, setError] = useState(null);

    const streamRef = useRef(initialStreamState());

    const onUpdateRef = useRef(onUpdate);
    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    const start = useCallback((conversationId) => {
        if (!conversationId) return;
        if (streamRef.current.conversationId === conversationId) return;
        streamRef.current = { ...initialStreamState(), conversationId };
        setError(null);
        setStatus(Status.STREAMING);
    }, []);

    /**
     * Stop the current stream: clears the conversationId filter so any
     * still-arriving chunks for the previous run are ignored, and resets
     * status to idle. The backend stream cannot be cancelled — this only
     * makes the client side stop reacting to it.
     */
    const stop = useCallback(() => {
        streamRef.current = initialStreamState();
        setError(null);
        setStatus(Status.IDLE);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('access-token');
        const deviceId = localStorage.getItem('device-id') || '';
        const socket = getSocket(token, deviceId);
        if (!socket) return undefined;

        const isOurConversation = (msg) =>
            msg?.conversationId && msg.conversationId === streamRef.current.conversationId;

        const handleChunk = (msg) => {
            if (!isOurConversation(msg)) return;
            const content = msg.content || '';
            if (!content) return;
            streamRef.current.summary += content;
            onUpdateRef.current?.({ content: streamRef.current.summary });
        };

        const handleDone = (msg) => {
            if (!isOurConversation(msg)) return;
            setStatus(Status.DONE);
        };

        const handleError = (msg) => {
            if (!isOurConversation(msg)) return;
            setError(msg?.error || 'AI generation failed');
            setStatus(Status.ERROR);
        };

        socket.on(SocketEvents.CHUNK, handleChunk);
        socket.on(SocketEvents.DONE, handleDone);
        socket.on(SocketEvents.ERROR, handleError);

        return () => {
            socket.off(SocketEvents.CHUNK, handleChunk);
            socket.off(SocketEvents.DONE, handleDone);
            socket.off(SocketEvents.ERROR, handleError);
            streamRef.current = initialStreamState();
        };
    }, []);

    return {
        start,
        stop,
        error,
        isStreaming: status === Status.STREAMING,
        isDone: status === Status.DONE,
        isError: status === Status.ERROR,
    };
};
